import React, {useMemo} from "react";
import ChartLoadingOverlay from "../../components/Utils/ChartLoadingOverlay";

const calcChangePercent = (current, previous) => {
    const cur = Number(current) || 0;
    const prev = Number(previous) || 0;
    if (prev === 0) return cur > 0 ? 100 : 0;
    return Math.round(((cur - prev) / prev) * 100);
};

const rowsToMap = (rows) =>
    Object.fromEntries((rows ?? []).map((row) => [row.name, Number(row.cnt) || 0]));

const pickCount = (byName, ...keys) => {
    for (const key of keys) {
        if (byName[key] != null) return byName[key];
    }
    return 0;
};

/** getInDecreaseRateOfError — data.days: [{ name: "전일"|"어제", cnt }, { name: "오늘"|"금일"|"당일", cnt }] */
const parseDailyError = (errorRateData) => {
    const rows = errorRateData?.days;
    if (!Array.isArray(rows) || rows.length === 0) {
        return {count: 0, changePercent: 0, hasData: false};
    }

    const byName = rowsToMap(rows);
    const current = pickCount(byName, "오늘", "금일", "당일");
    const previous = pickCount(byName, "전일", "어제");

    return {
        count: current,
        changePercent: calcChangePercent(current, previous),
        hasData: true,
    };
};

const parseMonthlyError = (errorRateData) => {
    const rows = errorRateData?.right;
    if (!Array.isArray(rows)) {
        return {count: 0, changePercent: 0};
    }

    const byName = rowsToMap(rows);
    const current = byName["이번달"] ?? 0;
    const previous = byName["전월"] ?? 0;

    return {
        count: current,
        changePercent: calcChangePercent(current, previous),
    };
};

function ErrorRateCard({title, count, changePercent, compareLabel, loading = false, placeholder = false}) {
    const hasTrend = !placeholder && changePercent != null;
    const isUp = hasTrend && changePercent > 0;
    const isDown = hasTrend && changePercent < 0;

    const trendClass = isUp
        ? "text-[#ef4444]"
        : isDown
          ? "text-[#3b82f6]"
          : "text-[#9ca3af]";

    const trendLabel = placeholder
        ? "-"
        : hasTrend
          ? `${isUp ? "↑ " : isDown ? "↓ " : ""}${Math.abs(changePercent)}%`
          : "-";

    return (
        <div className="relative flex min-h-[120px] flex-col justify-between rounded-lg border border-[#e5e7eb] bg-white p-[16px] md:h-full md:min-h-0">
            {loading && <ChartLoadingOverlay />}

            <div className="flex items-center justify-between gap-3">
                <h4 className="shrink-0 text-[21px] font-bold leading-tight text-[#111827]">
                    {title}
                </h4>
                <div className="flex items-baseline gap-1.5">
                    <span className="text-[42px] font-bold leading-none tracking-tight text-[#3b82f6]">
                        {placeholder ? "-" : count}
                    </span>
                    <span className="text-[20px] font-medium text-[#111827]">건</span>
                </div>
            </div>

            <div className="flex items-end justify-between gap-3">
                <span className={`text-[20px] font-semibold leading-none ${trendClass}`}>
                    {trendLabel}
                </span>
                <span className="shrink-0 text-[16px] text-[#6b7280]">{compareLabel}</span>
            </div>
        </div>
    );
}

/** 일간 장애비율 — getInDecreaseRateOfError.data.days (오늘·전일) */
export function DailyErrorRateCard({data, loading = false}) {
    const {count, changePercent, hasData} = useMemo(() => parseDailyError(data), [data]);

    return (
        <ErrorRateCard
            title="일간 장애비율"
            count={count}
            changePercent={changePercent}
            compareLabel="전일대비"
            loading={loading}
            placeholder={!hasData && !loading}
        />
    );
}

/** 월간 장애비율 — getInDecreaseRateOfError (이번달·전월·2달 전) */
export function MonthlyErrorRateCard({data, loading = false}) {
    const {count, changePercent} = useMemo(() => parseMonthlyError(data), [data]);

    return (
        <ErrorRateCard
            title="월간 장애비율"
            count={count}
            changePercent={changePercent}
            compareLabel="전월대비"
            loading={loading}
        />
    );
}

export default MonthlyErrorRateCard;
